import React from 'react';
import { 
  Rocket, 
  Users, 
  Shield, 
  Zap, 
  Globe, 
  RefreshCw,
  Check
} from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: Rocket,
      title: 'Rapid Development',
      description: 'Agile methodologies and modern frameworks enable us to deliver high-quality applications faster.',
      benefits: [
        'Iterative development process',
        'Regular milestone deliveries',
        'Continuous feedback integration',
        'Fast time-to-market'
      ]
    },
    {
      icon: Users,
      title: 'User-Centric Design',
      description: 'Beautiful, intuitive interfaces designed with your users in mind for maximum engagement.',
      benefits: [
        'Research-driven UI/UX',
        'Accessibility compliance',
        'Mobile-first approach',
        'User testing & optimization'
      ]
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-grade security measures to protect your data and ensure regulatory compliance.',
      benefits: [
        'End-to-end encryption',
        'Regular security audits',
        'GDPR/CCPA compliance',
        'Penetration testing'
      ]
    },
    {
      icon: Zap,
      title: 'Peak Performance',
      description: 'Optimized code and infrastructure for lightning-fast load times and smooth operations.',
      benefits: [
        'Code optimization',
        'CDN integration',
        'Database indexing',
        'Caching strategies'
      ]
    },
    {
      icon: Globe,
      title: 'Global Scalability',
      description: 'Built to grow with your business, from startup to enterprise-level operations.',
      benefits: [
        'Auto-scaling infrastructure',
        'Load balancing',
        'Multi-region deployment',
        'Microservices architecture'
      ]
    },
    {
      icon: RefreshCw,
      title: 'Continuous Support',
      description: 'Ongoing maintenance, updates, and support to keep your application running smoothly.',
      benefits: [
        '24/7 monitoring',
        'Regular updates',
        'Bug fixes & patches',
        'Performance optimization'
      ]
    }
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand/5 to-transparent"></div>

      <div className="relative z-10 max-w-7xl mx-auto content-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Why Choose <span className="brand-gradient">HyperionAI</span>
          </h2>
          <p className="text-lg text-dark-muted max-w-2xl mx-auto">
            We combine technical excellence with business acumen to deliver applications that drive real results.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="relative group"
              >
                {/* Card */}
                <div className="bg-dark-surface border border-dark-border rounded-xl p-6 h-full hover:border-brand/30 transition-all duration-300">
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-brand to-brand-light rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-dark-muted text-sm leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>

                  {/* Benefits List */}
                  <div className="space-y-2 mt-4">
                    {feature.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-dark-muted">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stats Section */}
        <div className="mt-20 bg-gradient-to-r from-brand/10 via-brand-light/10 to-highlight/10 rounded-2xl p-8 md:p-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold brand-gradient mb-2">10+</div>
              <div className="text-sm text-dark-muted">Years of Excellence</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold brand-gradient mb-2">50+</div>
              <div className="text-sm text-dark-muted">Expert Developers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold brand-gradient mb-2">200+</div>
              <div className="text-sm text-dark-muted">Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold brand-gradient mb-2">15+</div>
              <div className="text-sm text-dark-muted">Countries Served</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;

